import React from "react";
import _ from "lodash";
import moment from "moment";
import submit from "submit-form";

import MonthGrid from "./components/MonthGrid";

import { isCoach } from "../../../utils/user-helpers";
import MonthWeek from "./components/MonthWeek";

import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import CalendarDuplicateWorkoutModal from "../../../parts/modals/calendar-duplicate-workout";
import CalendarMoveWorkoutModal from "../../../parts/modals/calendar-move-workout";
import CalendarAddExerciseModal from "../../../parts/modals/calendar-add-exercise";
import CalendarShiftWorkoutModal from "../../../parts/modals/calendar-shift-workout";
import VideoModal from "../../../parts/modals/video";
import scriptLoader from "../../../utils/scriptLoader";

import Modal from "../../../parts/modal";
import SvgIcon from "../../../parts/SvgIcon";

const {
  DATE_FORMATS: { DATE_YEAR_FORMAT, DATE_DAY_FORMAT },
} = require("../../../constants");

class View extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      showFooter: true,
      selectedDates: [],
      loaded: false,
      scheduledSessions: context.locals.scheduledSessions,
      dragging: false,
      draggingFrom: null,
      draggingTo: null,
      expanded: true,
    };

    this.handleSelectDate = this.handleSelectDate.bind(this);
    this.handleRemoveMultiple = this.handleRemoveMultiple.bind(this);
    this.handleDeselectAll = this.handleDeselectAll.bind(this);
    this.handleSelectAll = this.handleSelectAll.bind(this);

    this.goToMyPrograms = this.goToMyPrograms.bind(this);
    this.goToContactUs = this.goToContactUs.bind(this);
    this.loadDroppable = this.loadDroppable.bind(this);
    this.updateWorkout = this.updateWorkout.bind(this);
    this.forceSessionsUpdate = this.forceSessionsUpdate.bind(this);
    this.drop = null;
  }

  loadDroppable() {
    this.drop = new Droppable.default(
      document.querySelectorAll(".Calendar-Month"),
      {
        draggable: ".day-draggable",
        dropzone: ".day-margin",
        handle: ".drag-icon",
        mirror: {
          constrainDimensions: true,
        },
      }
    );

    this.drop.on("droppable:start", () => {
      this.setState({
        dragging: true,
      });
    });

    this.drop.on("droppable:stop", (evt) => {
      const { dropzone, dragEvent } = evt.data;
      const { originalSource } = dragEvent;
      const draggingFrom = originalSource.id;
      const draggingTo = dropzone.id.replace("container-", "");
      if (draggingFrom === draggingTo) {
        evt.cancel();
        return;
      }
      this.setState({
        dragging: false,
        draggingFrom,
        draggingTo,
      });

      this.forceSessionsUpdate();

      const newDiv = document.getElementById("container-".concat(draggingTo));

      // Since we need to force the state updating, we will have 2 workouts in the new day (duplicated)
      // One belongs to the scheduledSessions state, and the other one is the one moved by Dropabble.js
      // With the timeout we give Droppable.js the time to move it and thus to have that
      // element available in the new component. Then we remove it since it is duplicated
      setTimeout(() => {
        // The "falsy" value will be the one moved by Droppable.js, since it will have the date
        // untouched, belonging to the old day. So we get the one with the "draggingFrom" date
        const toRemoveChild = document.getElementById(draggingFrom);
        newDiv.removeChild(toRemoveChild);
      }, 4);

      this.updateWorkout(draggingFrom, draggingTo);
    });
  }

  updateWorkout(dateFrom, dateTo) {
    const athlete =
      _.get(this.locals, "query.athlete") || _.get(this.locals, "athlete._id");

    submit(
      `/actions/exercise/move-day?proxyAthlete=${athlete}&reqSource=calendar&dnd=true`,
      {
        method: "POST",
        body: {
          from: moment.utc(dateFrom, DATE_DAY_FORMAT).startOf("day").toJSON(),
          to: moment(dateTo, DATE_DAY_FORMAT).startOf("day").toJSON(),
          type: "move",
        },
      }
    );
  }

  forceSessionsUpdate() {
    const { draggingFrom, draggingTo, scheduledSessions } = this.state;

    const tempSessions = scheduledSessions;
    const oldDate = tempSessions[draggingFrom];

    if (oldDate) {
      delete tempSessions[draggingFrom];
      oldDate.date = moment(draggingTo, DATE_DAY_FORMAT).format();
      tempSessions[draggingTo] = oldDate;

      this.setState({
        scheduledSessions: tempSessions,
      });
    }
  }

  componentWillReceiveProps(nextProps, nextContext) {
    const { locals } = nextContext;
    const { scheduledSessions } = locals;
    if (scheduledSessions !== this.state.scheduledSessions) {
      this.setState({ scheduledSessions });
    }
  }

  goToMyPrograms(e) {
    e.preventDefault();

    setTimeout(
      () =>
        submit("/dashboard/program-builder", {
          method: "GET",
        }),
      300
    );
  }

  goToContactUs(e) {
    e.preventDefault();

    setTimeout(
      () =>
        submit("/dashboard/support/contact", {
          method: "GET",
        }),
      300
    );
  }

  goToExerciseLibrary(e) {
    e.preventDefault();
    this.closeAthleteWelcomeForCoach();
    setTimeout(
      () =>
        submit("/dashboard/settings/exercise-library", {
          method: "GET",
        }),
      300
    );
  }

  componentDidMount() {
    const { activeUser, activeUserToRequest } = this.context.locals;
    scriptLoader(
      "https://cdn.jsdelivr.net/npm/@shopify/draggable@1.0.0-beta.12/lib/droppable.js",
      this.loadDroppable,
      "droppableScript"
    );

    // the property `welcomeVideoShown` was moved into `welcomeVideos`. But some users still have this property
    // out of this new object. So we need to check both
    if (
      activeUserToRequest.pushAccountInfo &&
      !activeUserToRequest.pushAccountInfo.accountLinkFinished
    ) {
      this.setState({ showAccountLinkVideo: true });
    }
  }

  handleSelectDate(e, dayString) {
    const selectedDates = this.state.selectedDates;

    if (selectedDates.indexOf(dayString) > -1) {
      selectedDates.splice(selectedDates.indexOf(dayString), 1);
    } else {
      selectedDates.push(dayString);
    }

    this.setState({ selectedDates, showFooter: Boolean(selectedDates.length) });
  }

  handleRemoveMultiple(e, singleDate = false) {
    if (confirm("Are you sure you want to remove the selected workout(s)?")) {
      e.preventDefault();

      const days = this.state.selectedDates.length
        ? this.state.selectedDates.map((date) =>
            moment(date).format(DATE_DAY_FORMAT)
          )
        : [moment(singleDate).format(DATE_DAY_FORMAT)];

      submit(`/actions/exercise/unschedule-workout?days=${days}`, {
        method: "POST",
        body: { days },
      });

      this.setState({
        selectedDates: [],
        showFooter: false,
      });
    }
  }

  handleDeselectAll() {
    this.setState({
      selectedDates: [],
    });
  }

  handleSelectAll() {
    const { scheduledSessions } = this.context.locals;
    const newSelectedDates = [];
    Object.keys(scheduledSessions).map((key) => {
      newSelectedDates.push(key);
    });

    this.setState({
      selectedDates: newSelectedDates,
    });
  }

  switchMonth(e, direction, activeMonth) {
    e.preventDefault();
    this.setState({ loaded: false, selectedDates: [], showFooter: false });
    submit(`/actions/refresh?`, {
      method: "GET",
      body: {
        month: activeMonth
          .clone()
          .add(direction, "months")
          .format(DATE_YEAR_FORMAT),
      },
    });
  }

  render() {
    const {
      activeMonth,
      query,
      startOfFirstWeek,
      scheduledSessions,
      activeUser: { isCoachEditing, startingCalendarDay },
    } = this.context.locals;

    const { selectedDates, showAccountLinkVideo } = this.state;

    const totalScheduledSessions = Object.keys(scheduledSessions).length;
    const allSelected = totalScheduledSessions === selectedDates.length;
    return (
      <main className="Calendar">
        <header>
          <section>
            <hgroup>
              {selectedDates.length > 0 ? (
                <>
                  {totalScheduledSessions > 0 && (
                    <button
                      data-button-type="secondary"
                      onClick={
                        allSelected
                          ? this.handleDeselectAll
                          : this.handleSelectAll
                      }
                    >
                      <SvgIcon
                        className="mr-2"
                        src={
                          allSelected
                            ? "/img/new-icons/checked-square.svg"
                            : "/img/new-icons/unchecked-square.svg"
                        }
                        noFilterOnLight={allSelected}
                      />
                      {allSelected ? "Deselect All" : "Select All"}
                    </button>
                  )}
                  <p className="p4">({selectedDates.length} selected)</p>
                </>
              ) : (
                <>
                  <a
                    href={`/actions/refresh?month=${moment().format(
                      DATE_YEAR_FORMAT
                    )}`}
                    role="button"
                    data-button-type="outline"
                  >
                    <span>This Month</span>
                  </a>
                  <div
                    className="expand"
                    onClick={() =>
                      this.setState({ expanded: !this.state.expanded })
                    }
                  >
                    <span>Expand Calendar</span>
                    {
                      <SvgIcon
                        src={`/img/new-icons/${
                          this.state.expanded ? "" : "un"
                        }checked-square.svg`}
                        className="icon-white"
                        alt="expand"
                        noFilterOnLight={this.state.expanded}
                      />
                    }
                  </div>
                </>
              )}
            </hgroup>
            <hgroup>
              <div role="month-handler">
                <div>
                  <button
                    data-button-type="text-white"
                    data-icon-only
                    onClick={(e) => this.switchMonth(e, -1, activeMonth)}
                  >
                    <SvgIcon src="/img/new-icons/arrow-left.svg" alt="prev" />
                  </button>
                  <span className="label">
                    {activeMonth.format("MMMM, YYYY")}
                  </span>
                  <button
                    data-button-type="text-white"
                    data-icon-only
                    onClick={(e) => this.switchMonth(e, 1, activeMonth)}
                  >
                    <SvgIcon src="/img/new-icons/arrow-right.svg" alt="next" />
                  </button>
                </div>
              </div>
            </hgroup>
            <hgroup>
              {selectedDates.length > 0 ? (
                <>
                  <button
                    data-button-type="secondary"
                    onClick={() => this.setState({ selectedDates: [] })}
                  >
                    Cancel
                  </button>
                  <button
                    data-button-type="secondary"
                    className="!text-[--danger]"
                    onClick={this.handleRemoveMultiple}
                    disabled={selectedDates.length === 0}
                  >
                    <SvgIcon
                      className="mr-2"
                      src={"/img/new-icons/red-trash.svg"}
                      noFilterOnLight={true}
                    />
                    Remove
                  </button>
                </>
              ) : (
                <ToggleLinksInput
                  name="view"
                  options={[
                    {
                      text: "Month",
                      value: "month",
                      href: `/actions/refresh?view=month&month=${
                        query.week
                          ? moment(query.week, DATE_DAY_FORMAT).format(
                              DATE_YEAR_FORMAT
                            )
                          : query.date
                          ? moment(query.date, DATE_DAY_FORMAT).format(
                              DATE_YEAR_FORMAT
                            )
                          : ""
                      }&week=&date=`,
                    },
                    {
                      text: "Week",
                      value: "week",
                      href: `/actions/refresh?view=week&week=${
                        moment().isSame(query.month, "month")
                          ? moment().format(DATE_DAY_FORMAT)
                          : moment(query.month, DATE_YEAR_FORMAT).format(
                              DATE_DAY_FORMAT
                            )
                      }&month=&date=`,
                    },
                    {
                      text: "Day",
                      value: "day",
                      href: `/actions/refresh?view=day&date=${
                        moment().isSame(query.month, "month")
                          ? moment().format(DATE_DAY_FORMAT)
                          : moment(query.month, DATE_YEAR_FORMAT).format(
                              DATE_DAY_FORMAT
                            )
                      }&month=&week=`,
                    },
                  ]}
                />
              )}
            </hgroup>
          </section>
        </header>

        <MonthGrid startDay={startingCalendarDay}>
          {_.times(6, (week) => {
            const startOfWeek = startOfFirstWeek.clone().add(week, "weeks");
            if (
              startOfWeek.month() === activeMonth.clone().month() ||
              startOfWeek.clone().endOf("week").month() ===
                activeMonth.clone().month()
            ) {
              return (
                <MonthWeek
                  activeMonth={activeMonth.clone()}
                  key={week}
                  startOfWeek={startOfWeek}
                  handleSelectDate={this.handleSelectDate}
                  selectedDates={this.state.selectedDates}
                  scheduledSessions={this.state.scheduledSessions}
                  handleRemoveMultiple={this.handleRemoveMultiple}
                  dragging={this.state.dragging}
                  expanded={this.state.expanded}
                />
              );
            }
          })}
        </MonthGrid>

        {isCoachEditing && (
          <div className="Coach-Edit">
            <img src="/img/icons/edit.svg" width="20" height="20" />
            Your coach is editing your calendar. You will not be able to save
            changes at this time.
          </div>
        )}

        <input type="radio" name="more" id="more-reset" />
      </main>
    );
  }
}

const CalendarMonth = (app) => {
  app.get("/", async ({ req, res, locals, load }) => {
    // there is no mobile view for the month calendar view so if we are on mobile
    // redirect to the week view whcih does have mobile styles.
    //     if (
    //       process.browser &&
    //       window.innerWidth &&
    //       Number(window.innerWidth) < 960
    //     ) {
    //       res.redirect("/dashboard/calendar?view=week");
    //     }

    const { query } = req;
    _.defaults(query, {
      month: moment().format(DATE_YEAR_FORMAT),
      view: "month",
    });
    const modal = _.get(query, "modal", {});

    locals.activeMonth = moment(query.month, DATE_YEAR_FORMAT);
    locals.startOfFirstWeek = locals.activeMonth
      .clone()
      .startOf("week")
      .weekday(locals.activeUser.startingCalendarDay || 0);
    locals.endOfLastWeek = locals.activeMonth
      .clone()
      .endOf("month")
      .endOf("week")
      .add(locals.activeUser.startingCalendarDay, "days");
    // Cache remove for MSB-411

    locals.activeExercises = await load("activeExercises", {
      start: locals.startOfFirstWeek,
      end: locals.endOfLastWeek,
    });
    locals.scheduledSessions = _.groupBy(locals.activeExercises, (e) => {
      return moment
        .utc((e.utcDate && e.utcDate.toString()) || e.date)
        .format(DATE_DAY_FORMAT);
    });
    const cycles = _.uniqBy(
      locals.activeExercises.filter((e) => e.cycle),
      "cycle.id"
    ).map((exercise) => exercise.cycle.id);
    locals.cycle = await load("cycles", cycles);

    locals.modal = {
      "calendar-add-exercise": <CalendarAddExerciseModal />,
      "calendar-duplicate-workout": <CalendarDuplicateWorkoutModal />,
      "calendar-move-workout": <CalendarMoveWorkoutModal locals={locals} />,
      "calendar-shift-workout": <CalendarShiftWorkoutModal />,
      video: <VideoModal />,
    }[modal.type];

    if (
      modal.type === "calendar-move-workout" ||
      modal.type === "calendar-duplicate-workout" ||
      modal.type === "calendar-shift-workout"
    ) {
      await load("activeExercises", {
        start: moment(0),
        end: moment("2049-12-31"),
      });
    }

    // first login
    if (query.firstLogin && !locals.activeUser.coach) {
      delete query.firstLogin;
      return res.redirect(`/dashboard/calendar?modal[type]=video`);
    }

    res.body = <View />;
  });
};

export default CalendarMonth;
